import React, { useState, useEffect } from 'react';
import { Palette, Type, Image, Save, X, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';

function BrandingConfigManager() {
  const [config, setConfig] = useState({
    site_name: 'SWARM',
    logo_url: '',
    strapline: 'Progressive Traffic Incident Reporting',
    primary_color: '#1a237e', // Deep blue
    secondary_color: '#f5f7fa', // Clean light background
    accent_color: '#ff6b35', // Vibrant accent
    font_family: 'Poppins', // Modern font
    font_weights: ['400', '500', '600', '700'],
    header_background: '#212a3f', // Dark blue header
    header_text_color: '#ffffff',
    footer_background: '#f5f7fa',
    footer_text_color: '#212a3f',
    button_primary_bg: '#1a237e',
    button_primary_text: '#ffffff',
    button_secondary_bg: '#e0e7ef',
    button_secondary_text: '#1a237e',
    card_background: '#ffffff',
    card_border_color: '#e0e7ef',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadBrandingConfig();
  }, []);

  const loadBrandingConfig = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.getBrandingConfig();
      const config = response.config || {};

      // Helper function to normalize hex colors
      const normalizeHex = color => {
        if (!color) return color;
        if (color.match(/^#[0-9A-F]{3}$/i)) {
          return color.replace(/^#([0-9A-F])([0-9A-F])([0-9A-F])$/i, '#$1$1$2$2$3$3');
        }
        return color;
      };

      setConfig({
        site_name: config.site_name || config.siteName || 'SWARM',
        logo_url: config.logo_url || config.logoUrl || '',
        strapline: config.strapline || 'Progressive Traffic Incident Reporting',
        primary_color: normalizeHex(config.primary_color || config.primaryColor || '#1a237e'),
        secondary_color: normalizeHex(config.secondary_color || config.secondaryColor || '#f5f7fa'),
        accent_color: normalizeHex(config.accent_color || config.accentColor || '#ff6b35'),
        font_family: config.font_family || config.fontFamily || 'Poppins',
        font_weights: config.font_weights || config.fontWeights || ['400', '500', '600', '700'],
        header_background: normalizeHex(
          config.header_background || config.headerBackground || '#212a3f',
        ),
        header_text_color: normalizeHex(
          config.header_text_color || config.headerTextColor || '#ffffff',
        ),
        footer_background: normalizeHex(
          config.footer_background || config.footerBackground || '#f5f7fa',
        ),
        footer_text_color: normalizeHex(
          config.footer_text_color || config.footerTextColor || '#212a3f',
        ),
        button_primary_bg: normalizeHex(
          config.button_primary_bg || config.buttonPrimaryBg || '#1a237e',
        ),
        button_primary_text: normalizeHex(
          config.button_primary_text || config.buttonPrimaryText || '#ffffff',
        ),
        button_secondary_bg: normalizeHex(
          config.button_secondary_bg || config.buttonSecondaryBg || '#e0e7ef',
        ),
        button_secondary_text: normalizeHex(
          config.button_secondary_text || config.buttonSecondaryText || '#1a237e',
        ),
        card_background: normalizeHex(config.card_background || config.cardBackground || '#ffffff'),
        card_border_color: normalizeHex(
          config.card_border_color || config.cardBorderColor || '#e0e7ef',
        ),
      });
    } catch (err) {
      console.error('Error loading branding config:', err);
      setError('Failed to load branding configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Ensure font_weights is always an array
      const payload = {
        ...config,
        font_weights: Array.isArray(config.font_weights)
          ? config.font_weights
          : typeof config.font_weights === 'string'
            ? JSON.parse(config.font_weights)
            : ['400', '500', '600', '700'],
      };
      console.log('Sending branding config data:', payload);
      await api.updateBrandingConfig(payload);
      setSuccess('Branding configuration updated successfully');
    } catch (err) {
      console.error('Error saving branding config:', err);
      setError('Failed to save branding configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (field, value) => {
    // Convert 3-digit hex to 6-digit hex if needed
    let normalizedValue = value;
    if (value && value.match(/^#[0-9A-F]{3}$/i)) {
      normalizedValue = value.replace(/^#([0-9A-F])([0-9A-F])([0-9A-F])$/i, '#$1$1$2$2$3$3');
    }
    setConfig(prev => ({ ...prev, [field]: normalizedValue }));
  };

  const handleFontWeightToggle = weight => {
    setConfig(prev => ({
      ...prev,
      font_weights: prev.font_weights.includes(weight)
        ? prev.font_weights.filter(w => w !== weight)
        : [...prev.font_weights, weight].sort(),
    }));
  };

  const getPreviewStyles = () => ({
    '--primary-color': config.primary_color,
    '--secondary-color': config.secondary_color,
    '--accent-color': config.accent_color,
    '--header-bg': config.header_background,
    '--header-text': config.header_text_color,
    '--footer-bg': config.footer_background,
    '--footer-text': config.footer_text_color,
    '--button-primary-bg': config.button_primary_bg,
    '--button-primary-text': config.button_primary_text,
    '--button-secondary-bg': config.button_secondary_bg,
    '--button-secondary-text': config.button_secondary_text,
    '--card-bg': config.card_background,
    '--card-border': config.card_border_color,
    fontFamily: config.font_family,
  });

  const fontOptions = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Poppins',
    'Montserrat',
    'Source Sans Pro',
    'Ubuntu',
    'Nunito',
    'Work Sans',
  ];

  const fontWeightOptions = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Branding Configuration</h2>
          <p className="text-gray-600 mt-1">
            Customize the site's appearance, colors, and branding elements
          </p>
        </div>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
        >
          {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <X className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <Save className="w-5 h-5 text-green-500" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {/* Live Preview */}
      {showPreview && (
        <div className="bg-white border border-gray-200 rounded-lg p-6" style={getPreviewStyles()}>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Live Preview</h3>

          {/* Header Preview */}
          <div
            className="p-4 rounded-lg mb-4"
            style={{ backgroundColor: 'var(--header-bg)', color: 'var(--header-text)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {config.logo_url && <img src={config.logo_url} alt="Logo" className="w-8 h-8" />}
                <div>
                  <h1 className="text-xl font-bold">{config.site_name}</h1>
                  {config.strapline && <p className="text-sm opacity-90">{config.strapline}</p>}
                </div>
              </div>
              <button
                className="px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--button-primary-bg)',
                  color: 'var(--button-primary-text)',
                }}
              >
                Sample Button
              </button>
            </div>
          </div>

          {/* Content Preview */}
          <div
            className="p-4 rounded-lg border mb-4"
            style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--card-border)',
            }}
          >
            <h4 className="font-semibold mb-2" style={{ color: 'var(--primary-color)' }}>
              Sample Content Card
            </h4>
            <p className="text-gray-600 mb-3">
              This is how your content will appear with the selected branding.
            </p>
            <div className="flex space-x-2">
              <button
                className="px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--button-primary-bg)',
                  color: 'var(--button-primary-text)',
                }}
              >
                Primary
              </button>
              <button
                className="px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--button-secondary-bg)',
                  color: 'var(--button-secondary-text)',
                }}
              >
                Secondary
              </button>
            </div>
          </div>

          {/* Footer Preview */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: 'var(--footer-bg)', color: 'var(--footer-text)' }}
          >
            <p className="text-sm text-center">
              © 2026{' '}
              <a
                href="https://anthemic-developments.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700"
              >
                anthemic-developments.com
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Branding */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Image className="w-5 h-5 mr-2" />
            Basic Branding
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
              <input
                type="text"
                value={config.site_name || ''}
                onChange={e => setConfig({ ...config, site_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                type="url"
                value={config.logo_url || ''}
                onChange={e => setConfig({ ...config, logo_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Strapline</label>
              <input
                type="text"
                value={config.strapline || ''}
                onChange={e => setConfig({ ...config, strapline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Your site's tagline or description"
              />
            </div>
          </div>
        </div>

        {/* Color Scheme */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Color Scheme
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={config.primary_color || '#1a237e'}
                  onChange={e => handleColorChange('primary_color', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.primary_color || '#1a237e'}
                  onChange={e => handleColorChange('primary_color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="#1976d2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Color
              </label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={config.secondary_color || '#f5f7fa'}
                  onChange={e => handleColorChange('secondary_color', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.secondary_color || '#f5f7fa'}
                  onChange={e => handleColorChange('secondary_color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="#f4f6fa"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={config.accent_color || '#ff6b35'}
                  onChange={e => handleColorChange('accent_color', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={config.accent_color || '#ff6b35'}
                  onChange={e => handleColorChange('accent_color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="#ff6b35"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Typography */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Type className="w-5 h-5 mr-2" />
            Typography
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
              <select
                value={config.font_family || 'Poppins'}
                onChange={e => setConfig({ ...config, font_family: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontOptions.map(font => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Weights</label>
              <div className="grid grid-cols-3 gap-2">
                {fontWeightOptions.map(weight => (
                  <label key={weight} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={(config.font_weights || []).includes(weight)}
                      onChange={() => handleFontWeightToggle(weight)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{weight}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Component Colors */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Component Colors</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Header */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Header</h4>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Background</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.header_background || '#212a3f'}
                    onChange={e => handleColorChange('header_background', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.header_background || '#212a3f'}
                    onChange={e => handleColorChange('header_background', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.header_text_color || '#ffffff'}
                    onChange={e => handleColorChange('header_text_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.header_text_color || '#ffffff'}
                    onChange={e => handleColorChange('header_text_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Footer</h4>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Background</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.footer_background || '#f5f7fa'}
                    onChange={e => handleColorChange('footer_background', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.footer_background || '#f5f7fa'}
                    onChange={e => handleColorChange('footer_background', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.footer_text_color || '#212a3f'}
                    onChange={e => handleColorChange('footer_text_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.footer_text_color || '#212a3f'}
                    onChange={e => handleColorChange('footer_text_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Primary Button</h4>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Background</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.button_primary_bg || '#1a237e'}
                    onChange={e => handleColorChange('button_primary_bg', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.button_primary_bg || '#1a237e'}
                    onChange={e => handleColorChange('button_primary_bg', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.button_primary_text || '#ffffff'}
                    onChange={e => handleColorChange('button_primary_text', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.button_primary_text || '#ffffff'}
                    onChange={e => handleColorChange('button_primary_text', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Cards */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Cards</h4>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Background</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.card_background || '#ffffff'}
                    onChange={e => handleColorChange('card_background', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.card_background || '#ffffff'}
                    onChange={e => handleColorChange('card_background', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Border Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={config.card_border_color || '#e0e7ef'}
                    onChange={e => handleColorChange('card_border_color', e.target.value)}
                    className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.card_border_color || '#e0e7ef'}
                    onChange={e => handleColorChange('card_border_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-50 font-medium"
          >
            {loading ? 'Saving...' : 'Save Branding Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BrandingConfigManager;
